import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CardMedia,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useSnackbar } from "notistack";
// Container is used in the info/success screens
import YodaplusLogo from "../../asset/home-logo1.png";
import {
  PUBLIC_FEEDBACK_FORM_API,
  PUBLIC_FEEDBACK_SUBMIT_API,
  PUBLIC_FEEDBACK_DRAFT_API,
} from "../../Service/Apis";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A single rating criterion surfaced to the client on the public feedback form. */
interface Parameter {
  parameter_id: string;
  label: string;
  /** Guidance copy displayed beneath the label to help the client understand what to rate. */
  help_text: string;
  /** When true, the client cannot submit without providing a rating for this parameter. */
  is_mandatory: boolean;
  /** Ascending sort index controlling parameter display order. */
  order: number;
}

/** Template and request metadata returned by the public API for a given token. */
interface FormData {
  employee_name: string;
  project_name: string;
  /** Maximum star value; drives how many stars the inline StarRating renders. */
  rating_scale: number;
  /** When true, the comments textarea is required before the form can be submitted. */
  comments_mandatory: boolean;
  parameters: Parameter[];
}

/**
 * All possible states the page can be in.
 * Each non-"form" and non-"loading" state renders a simple informational screen
 * instead of the interactive form.
 */
type PageState =
  | "loading"
  | "form"
  | "success"
  | "invalid_link"
  | "expired"
  | "already_submitted"
  | "throttled"
  | "error";

// ---------------------------------------------------------------------------
// StarRating (inline, used in public form – no auth context)
// ---------------------------------------------------------------------------

/** Props for the lightweight interactive star-rating widget used on the public form. */
interface StarRatingProps {
  /** Total number of stars to render (matches the template's `rating_scale`). */
  scale: number;
  /** Currently selected rating value; 0 means no rating chosen yet. */
  value: number;
  /** Called with the star index (1-based) when the client clicks a star. */
  onChange: (v: number) => void;
}

/**
 * Lightweight interactive star-rating component inlined into the public feedback form.
 * A separate implementation from the authenticated `StarRating` component is used here
 * to avoid importing anything that depends on the Redux store or auth context,
 * keeping this page fully accessible without authentication.
 *
 * Hover state is tracked locally to highlight stars as the cursor moves over them.
 */
const StarRating: React.FC<StarRatingProps> = ({ scale, value, onChange }) => {
  const [hovered, setHovered] = useState<number>(0);
  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {Array.from({ length: scale }, (_, i) => i + 1).map((star) => (
        <Box
          key={star}
          component="span"
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star)}
          sx={{
            cursor: "pointer",
            fontSize: 28,
            color: star <= (hovered || value) ? "#f5a623" : "#ccc",
            transition: "color 0.15s",
            userSelect: "none",
          }}
        >
          ★
        </Box>
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Public-facing feedback form reached via a one-time tokenised link sent to clients by email.
 * This page intentionally has no authentication requirement.
 *
 * Lifecycle:
 * 1. The token is extracted from the URL and used to fetch the form's structure and any
 *    existing draft from the backend.
 * 2. If a draft exists, ratings and comments are pre-populated and the user is notified.
 * 3. The client fills in ratings for each template parameter and an overall rating,
 *    optionally adding comments.
 * 4. The form can be saved as a draft mid-way (progress is persisted server-side and
 *    restored on re-open) or submitted definitively.
 * 5. After submission the page transitions to a "success" state; the link cannot be used again.
 *
 * Error states handled: invalid/missing token, expired link, duplicate submission, rate limiting.
 */
const CustomerFeedbackForm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [pageState, setPageState] = useState<PageState>("loading");
  const [formData, setFormData] = useState<FormData | null>(null);
  const [paramRatings, setParamRatings] = useState<Record<string, number>>({});
  const [overallRating, setOverallRating] = useState<number>(0);
  const [comments, setComments] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [draftSaved, setDraftSaved] = useState<boolean>(false);

  // -------------------------------------------------------------------------
  // Load form on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!token) {
      setPageState("invalid_link");
      return;
    }
    axios
      .get(PUBLIC_FEEDBACK_FORM_API(token))
      .then((res) => {
        setFormData(res.data);
        setPageState("form");
        // Restore saved draft if the backend returned one
        const draft = res.data.draft;
        if (draft) {
          if (draft.parameter_ratings) setParamRatings(draft.parameter_ratings);
          if (draft.overall_rating) setOverallRating(draft.overall_rating);
          if (draft.additional_comments) setComments(draft.additional_comments);
          enqueueSnackbar("Your progress has been restored.", {
            variant: "info",
            autoHideDuration: 4000,
          });
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        const code = err?.response?.data?.error;
        if (status === 404 || code === "invalid_link") setPageState("invalid_link");
        else if (status === 410 && code === "link_expired") setPageState("expired");
        else if (status === 410 && code === "already_submitted") setPageState("already_submitted");
        else if (status === 429) setPageState("throttled");
        else setPageState("error");
      });
  }, [token]);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  /**
   * Returns true only when every mandatory parameter has a non-zero rating,
   * the overall rating is set, and (if `comments_mandatory`) the comments field
   * is not blank. Used to gate the submit button.
   */
  const allMandatoryFilled = (): boolean => {
    if (!formData) return false;
    for (const p of formData.parameters) {
      if (p.is_mandatory && !paramRatings[p.parameter_id]) return false;
    }
    if (!overallRating) return false;
    if (formData.comments_mandatory && !comments.trim()) return false;
    return true;
  };

  /**
   * Saves the current ratings and comments as a server-side draft without
   * finalising the submission. The draft is automatically restored the next
   * time the client opens the same link. The "saved" confirmation banner
   * auto-dismisses after 3 seconds to avoid cluttering the form.
   */
  const handleSaveDraft = async () => {
    if (!token) return;
    setSavingDraft(true);
    try {
      await axios.post(PUBLIC_FEEDBACK_DRAFT_API(token), {
        parameter_ratings: paramRatings,
        overall_rating: overallRating || null,
        additional_comments: comments,
      });
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch {
      enqueueSnackbar("Failed to save draft. Please try again.", {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setSavingDraft(false);
    }
  };

  /**
   * Finalises the feedback submission. On success the page transitions to the
   * "success" state permanently. Specific API error codes (already_submitted,
   * link_expired, throttled) transition to dedicated informational screens rather
   * than surfacing a generic error message.
   */
  const handleSubmit = async () => {
    if (!token || !formData) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      await axios.post(PUBLIC_FEEDBACK_SUBMIT_API(token), {
        parameter_ratings: paramRatings,
        overall_rating: overallRating,
        additional_comments: comments,
      });
      setPageState("success");
    } catch (err: any) {
      const status = err?.response?.status;
      const code = err?.response?.data?.error;
      if (code === "already_submitted") {
        setPageState("already_submitted");
      } else if (code === "link_expired") {
        setPageState("expired");
      } else if (status === 429) {
        setPageState("throttled");
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  /**
   * Renders a centred, text-only screen for terminal page states (expired, invalid
   * link, already submitted, etc.) where no interactive form should be shown.
   */
  const renderInfoScreen = (title: string, message: string) => (
    <Container maxWidth="sm" sx={{ mt: 10, textAlign: "center" }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      <Typography color="text.secondary">{message}</Typography>
    </Container>
  );

  if (pageState === "loading")
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
        <CircularProgress />
      </Box>
    );

  if (pageState === "invalid_link")
    return renderInfoScreen("Invalid Link", "This feedback link is not valid.");

  if (pageState === "expired")
    return renderInfoScreen("Link Expired", "This link is no longer valid. Please contact your manager.");

  if (pageState === "already_submitted")
    return renderInfoScreen(
      "Already Submitted",
      "Feedback has already been submitted for this request. Thank you!"
    );

  if (pageState === "throttled")
    return renderInfoScreen(
      "Too Many Requests",
      "You have opened this link too many times. Please wait a few minutes and try again."
    );

  if (pageState === "error")
    return renderInfoScreen("Error", "Something went wrong. Please try again later.");

  if (pageState === "success")
    return (
      <Container maxWidth="sm" sx={{ mt: 10, textAlign: "center" }}>
        <Typography variant="h5" fontWeight={700} color="success.main" gutterBottom>
          Thank You!
        </Typography>
        <Typography color="text.secondary">
          Your feedback has been recorded successfully.
        </Typography>
      </Container>
    );

  // pageState === "form"
  if (!formData) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#eef7ee",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* ── Left branding panel — hidden on mobile/tablet, shown on desktop ── */}
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          p: 5,
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Logo */}
        <CardMedia
          component="img"
          src={YodaplusLogo}
          alt="Yodaplus"
          sx={{ height: 44, width: "auto", objectFit: "contain", alignSelf: "flex-start" }}
        />

        {/* Tagline + illustration — centred as one block */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h3"
            fontWeight={800}
            lineHeight={1.2}
            sx={{ color: "#1a1a1a", mb: 1.5, fontFamily: "Poppins, sans-serif" }}
          >
            We value your
            <br />
            <Box
              component="span"
              sx={{
                background: "linear-gradient(90deg, #1a1a1a 0%, #2e7d32 60%, #66bb6a 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              feedback
            </Box>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Help us grow by sharing your experience.
          </Typography>

          {/* Chat-bubble illustration */}
          <svg viewBox="0 0 300 290" width="300" height="290" xmlns="http://www.w3.org/2000/svg">
            {/* larger circle backdrop */}
            <circle cx="150" cy="150" r="135" fill="#c8edc8" />
            {/* sparkle diamonds — shifted to match new center */}
            <polygon points="238,55 242,64 238,73 234,64" fill="#81c784" />
            <polygon points="268,108 272,116 268,124 264,116" fill="#81c784" opacity="0.8" />
            <polygon points="52,168 55,175 52,182 49,175" fill="#81c784" opacity="0.7" />
            <polygon points="32,100 35,108 32,116 29,108" fill="#81c784" opacity="0.6" />
            {/* all bubbles shifted +20x +20y to stay centred in the bigger circle */}
            <g transform="translate(20,20)">
              {/* left chat bubble */}
              <rect x="42" y="85" width="88" height="68" rx="14" fill="#66bb6a" />
              <polygon points="62,153 54,172 82,153" fill="#66bb6a" />
              <rect x="56" y="105" width="60" height="8" rx="4" fill="white" opacity="0.9" />
              <rect x="56" y="120" width="44" height="8" rx="4" fill="white" opacity="0.75" />
              {/* smiley chat bubble */}
              <rect x="128" y="68" width="80" height="70" rx="14" fill="#81c784" />
              <polygon points="190,138 200,158 172,138" fill="#81c784" />
              <circle cx="154" cy="98" r="5" fill="white" />
              <circle cx="180" cy="98" r="5" fill="white" />
              <path d="M152 112 Q167 124 184 112" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
              {/* bottom chat bubble */}
              <rect x="76" y="150" width="92" height="60" rx="14" fill="#4caf50" />
              <polygon points="96,150 86,132 114,150" fill="#4caf50" />
              <rect x="90" y="167" width="64" height="8" rx="4" fill="white" opacity="0.9" />
              <rect x="90" y="182" width="46" height="8" rx="4" fill="white" opacity="0.75" />
            </g>
          </svg>
        </Box>
      </Box>

      {/* ── Right form panel ── */}
      <Box
        sx={{
          width: { xs: "100%", md: "58%" },
          minHeight: "100vh",
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "center",
          p: { xs: 2, sm: 3, md: 5 },
          boxSizing: "border-box",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 620,
            p: { xs: 2.5, sm: 3, md: 4 },
            borderRadius: 3,
            border: "1px solid #e0e0e0",
            boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
            backgroundColor: "#ffffff",
            my: { xs: 3, md: 0 },
          }}
        >
          {/* Logo — only visible on mobile/tablet where left panel is hidden */}
          <Box sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", mb: 3 }}>
            <CardMedia
              component="img"
              src={YodaplusLogo}
              alt="Yodaplus"
              sx={{ height: 40, width: "auto", objectFit: "contain" }}
            />
          </Box>

          {/* Header */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="h5" fontWeight={700} color="#2e7d32" fontSize={{ xs: "1.2rem", sm: "1.4rem", md: "1.5rem" }}>
              Client Feedback Request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formData.employee_name}
              {formData.project_name ? ` — ${formData.project_name}` : ""}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* Parameter ratings */}
          {formData.parameters.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {[...formData.parameters]
                .sort((a, b) => a.order - b.order)
                .map((param) => (
                  <Box key={param.parameter_id} sx={{ mb: 2.5 }}>
                    <Typography variant="body1" fontWeight={700} fontSize="1.05rem">
                      {param.label}
                      {param.is_mandatory && (
                        <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                          *
                        </Box>
                      )}
                    </Typography>
                    {param.help_text && (
                      <Typography variant="body2" color="text.secondary" fontSize="0.9rem" sx={{ mb: 0.5 }}>
                        {param.help_text}
                      </Typography>
                    )}
                    <StarRating
                      scale={formData.rating_scale}
                      value={paramRatings[param.parameter_id] ?? 0}
                      onChange={(v) =>
                        setParamRatings((prev) => ({ ...prev, [param.parameter_id]: v }))
                      }
                    />
                  </Box>
                ))}
            </Box>
          )}

          <Divider sx={{ mb: 2.5 }} />

          {/* Overall rating */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body1" fontWeight={700} fontSize="1.05rem" gutterBottom>
              Overall Rating{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <StarRating
              scale={formData.rating_scale}
              value={overallRating}
              onChange={setOverallRating}
            />
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* Additional comments */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={700} fontSize="1.05rem" gutterBottom>
              Additional Comments
              {formData.comments_mandatory && (
                <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
                  *
                </Box>
              )}
            </Typography>
            <TextField
              multiline
              minRows={4}
              fullWidth
              placeholder="Share any additional feedback…"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              variant="outlined"
            />
          </Box>

          {/* Draft saved confirmation */}
          <Collapse in={draftSaved}>
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              Draft saved! Your progress has been saved and will be restored if you return to this link.
            </Alert>
          </Collapse>

          {submitError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {submitError}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
            <Button
              variant="outlined"
              size="large"
              onClick={handleSaveDraft}
              disabled={submitting || savingDraft}
              startIcon={savingDraft ? <CircularProgress size={18} /> : <SaveIcon />}
              sx={{
                borderRadius: 2,
                borderColor: "#2e7d32",
                color: "#2e7d32",
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                py: 1.4,
                flex: { sm: "0 0 auto" },
                whiteSpace: "nowrap",
                "&:hover": { borderColor: "#1b5e20", backgroundColor: "#f1f8f1" },
              }}
            >
              {savingDraft ? "Saving..." : "Save as Draft"}
            </Button>

            <Button
              variant="contained"
              fullWidth
              size="large"
              disabled={!allMandatoryFilled() || submitting || savingDraft}
              onClick={handleSubmit}
              sx={{
                borderRadius: 2,
                backgroundColor: "#2e7d32",
                "&:hover": { backgroundColor: "#1b5e20" },
                textTransform: "none",
                fontSize: "1rem",
                fontWeight: 600,
                py: 1.4,
              }}
            >
              {submitting ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Submit Feedback →"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CustomerFeedbackForm;
